import { ActionType, AudienceId, parseEntityIdSafe } from "@sp-crm/core";
import { AudienceShow } from "components/audience/audience-show";
import React from "react";
import { Redirect, Route, Switch } from "react-router";
import {
    useFeature,
    useIsAllowed,
    useIsAllowedInWholeTenant,
} from "store/selectors/hooks";
import { ManageIntegrationAccounts } from "./manage-integration-accounts";
import { ManageIntegrationsConstantContact } from "./manage-integrations-constant-contact";
import { ManageIntegrationsMailchimp } from "./manage-integrations-mailchimp";
import { ManageIntegrationsNav } from "./manage-integrations-nav";
import { ManageIntegrationsQuickBooks } from "./manage-integrations-quick-books";
import { ManageIntegrationsTwilio } from "./manage-integrations-twilio";
import { ManageIntegrationsWellSky } from "./manage-integrations-wellsky";
import { ManageWebhookDetail } from "./manage-webhook-detail";
import { ManageWebhooks } from "./manage-webhooks";

export const ManageIntegrations: React.FC<unknown> = () => {
    const manageIntegrationsAllowed = useIsAllowed(ActionType.ManageIntegrations);
    const wholeTenantAccess = useIsAllowedInWholeTenant(ActionType.ManageIntegrations);
    const isPlacementEnabled = useFeature("placement");
    const isWellSkyEnabled = useFeature("wellSkyIntegration");
    const defaultRoute = wholeTenantAccess
        ? "/settings/integrations/accounts"
        : isPlacementEnabled
          ? "/settings/integrations/quickbooks"
          : "/settings/integrations/accounts";
    if (!manageIntegrationsAllowed) {
        return null;
    }
    return (
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-5">
            <div className="lg:col-span-3">
                <ManageIntegrationsNav />
            </div>
            <div className="lg:col-span-9 pb-4">
                <Switch>
                    <Route
                        path="/settings/integrations/accounts"
                        component={ManageIntegrationAccounts}
                    />
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/quickbooks"
                            component={ManageIntegrationsQuickBooks}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/webhooks/:webhookId"
                            component={ManageWebhookDetail}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/webhooks"
                            component={ManageWebhooks}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/mailchimp/audiences/:audienceId/:subtab?"
                            render={props => {
                                const audienceIdParam = props.match.params.audienceId;
                                const subtab = props.match.params.subtab || "recipients";
                                const audienceId =
                                    parseEntityIdSafe<AudienceId>(
                                        audienceIdParam,
                                    ).getOrElse(null);

                                if (!audienceId) {
                                    return (
                                        <Redirect to="/settings/integrations/mailchimp" />
                                    );
                                }

                                return (
                                    <AudienceShow
                                        audienceId={audienceId}
                                        subtab={subtab}
                                    />
                                );
                            }}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/mailchimp"
                            render={props => (
                                <ManageIntegrationsMailchimp
                                    match={{
                                        path: props.match.path,
                                        params: {
                                            subtab: "audiences",
                                        },
                                    }}
                                />
                            )}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/constant-contact/audiences/:audienceId/:subtab?"
                            render={props => {
                                const audienceIdParam = props.match.params.audienceId;
                                const subtab = props.match.params.subtab || "recipients";
                                const audienceId =
                                    parseEntityIdSafe<AudienceId>(
                                        audienceIdParam,
                                    ).getOrElse(null);

                                if (!audienceId) {
                                    return (
                                        <Redirect to="/settings/integrations/constant-contact" />
                                    );
                                }

                                return (
                                    <AudienceShow
                                        audienceId={audienceId}
                                        subtab={subtab}
                                    />
                                );
                            }}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/constant-contact"
                            render={props => (
                                <ManageIntegrationsConstantContact
                                    match={{
                                        path: props.match.path,
                                        params: {
                                            subtab: "audiences",
                                        },
                                    }}
                                />
                            )}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/integrations/twilio"
                            component={ManageIntegrationsTwilio}
                        />
                    )}
                    {isWellSkyEnabled && (
                        <Route
                            path="/settings/integrations/wellsky-personal-care"
                            component={ManageIntegrationsWellSky}
                        />
                    )}
                    <Route
                        path="/settings/integrations"
                        render={() => <Redirect to={defaultRoute} />}
                    />
                </Switch>
            </div>
        </div>
    );
};
